import {
  Autocomplete,
  AutocompleteInputChangeReason,
  Grid,
  Typography,
  debounce,
} from "@mui/material";
import { isFulfilled } from "@reduxjs/toolkit";
import { useEffect, useState } from "react";
import { Controller, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { JOB_DESCRIPTION_MAX_LENGTH } from "../../../app/constants";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import CommonTextField from "../../../common/components/functional/commonTextField/CommonTextField";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import isEmpty from "../../../common/helpers/isEmpty";
import i18n from "../../../i18n";
import { LanguageSkillCode } from "../../talents/talentAPI";
import { workTitlesAutoComplete } from "../customerWorkLocationsSlice";

type Props = {
  values?: {
    workTitle: string;
    fi: string;
    en: string;
  };
  onClose: () => void;
};

const DescriptionsForm = ({ values, onClose }: Props) => {
  const [workTitleSearchTerm, setWorkTitleSearchTerm] = useState("");
  const [workTitleOptions, setWorkTitleOptions] = useState<any[]>([]);

  const { getValues: getDefaultValues, setValue } = useFormContext();

  const language = i18n.language.substring(0, 2) as LanguageSkillCode;

  const { t } = useTranslation();
  const { workTitle: workTitlesObject } = useAppSelector(
    (state) => state.localized,
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (workTitleSearchTerm) {
      dispatch(
        workTitlesAutoComplete({ term: workTitleSearchTerm, language }),
      ).then((action) => {
        if (isFulfilled(action)) {
          setWorkTitleOptions(action.payload);
        }
      });
    }
  }, [workTitleSearchTerm, dispatch, language]);

  const onInputChange = debounce(
    (term: string, reason?: AutocompleteInputChangeReason) => {
      if (reason === "input") {
        setWorkTitleSearchTerm(term);
      }
    },
    400,
  );

  const defaultValues = {
    workTitle: values
      ? {
          id: values?.workTitle || "",
          name: workTitlesObject[values?.workTitle] || "",
        }
      : null,
    fi: values?.fi || "",
    en: values?.en || "",
  };

  const {
    control,
    getValues,
    formState: { dirtyFields, isDirty, errors },
  } = useForm({
    defaultValues,
    mode: "onChange",
  });

  // values from workLocationsForm
  const info = getDefaultValues("info");

  const onSave = () => {
    // values from descriptionsForm
    const workTitle = getValues("workTitle") as { id: string; name: string };
    const fi = getValues("fi");
    const en = getValues("en");

    const updatedInfo = {
      ...info,
      [workTitle.id]: { fi, en },
    };

    // updated info values in workLocationsForm
    setValue("info", updatedInfo, { shouldDirty: true });
    onClose();
  };

  const onCancel = () => onClose();

  const saveDisabled = values
    ? !isDirty || !isEmpty(errors)
    : Object.keys(dirtyFields).length !== 3 || !isEmpty(errors);

  return (
    <DialogContentWrapper
      title={t("customers:workLocationNS.descriptionModalTitle")}
      margin
      onCancel={onCancel}
      onSave={onSave}
      saveDisabled={saveDisabled}
    >
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("common:workTitle")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        <Controller
          name="workTitle"
          control={control}
          rules={{ required: true }}
          render={({ field: { value, onChange } }) => (
            // TODO: should it be possible to add new work titles aka freesolo?
            <Autocomplete
              fullWidth
              value={value || null}
              options={workTitleOptions.sort((a, b) =>
                a.name.localeCompare(b.name),
              )}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onInputChange={(_, newValue, reason) =>
                onInputChange(newValue, reason)
              }
              onChange={(_, newValue) => onChange(newValue)}
              renderInput={(params) => (
                <CommonTextField {...params} error={!!errors.workTitle} />
              )}
            />
          )}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("order:jobDescriptionFI")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        <Controller
          name="fi"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              multiline
              size="medium"
              minRows={4}
              maxRows={4}
              fullWidth
              error={!!errors.fi}
              inputProps={{ maxLength: JOB_DESCRIPTION_MAX_LENGTH }}
            />
          )}
        />
      </Grid>
      <Grid item container xs={3} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {t("order:jobDescriptionEN")}
        </Typography>
      </Grid>
      <Grid item container xs={9} alignItems="center">
        <Controller
          name="en"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <CommonTextField
              {...field}
              multiline
              size="medium"
              minRows={4}
              maxRows={4}
              fullWidth
              error={!!errors.en}
              inputProps={{ maxLength: JOB_DESCRIPTION_MAX_LENGTH }}
            />
          )}
        />
      </Grid>
    </DialogContentWrapper>
  );
};

export default DescriptionsForm;
